import checkWithinPolygon from 'point-in-geopolygon';
import {
  DEFAULT_FLOOR_HEIGHT_METERS,
  GEOJSON_SITE_URL,
  FOOTPRINT_DEFAULT_FILL_COLOR,
  FOOTPRINT_DEFAULT_BORDER_COLOR,
} from './constants';
import type {
  PolygonFeature,
  TileFeature,
  TileProperties,
  FloorEstimationResult,
} from './types';

export { PURE_WIRELESS_URL, GEOJSON_SITE_URL } from './constants';

/**
 * Parse floor height string in format "Xft-Yin" to meters.
 * Examples: "10ft-0in", "12ft-6in", "9ft-3in"
 *
 * :param floorHeight: Floor height string in format "Xft-Yin"
 * :return: Height in meters
 */
export const parseFloorHeightToMeters = (
  floorHeight: string | undefined,
): number => {
  if (!floorHeight) return DEFAULT_FLOOR_HEIGHT_METERS;

  const match = floorHeight.match(/(\d+)ft-(\d+)in/i);
  if (!match) return DEFAULT_FLOOR_HEIGHT_METERS;

  const feet = parseInt(match[1], 10);
  const inches = parseInt(match[2], 10);

  // Convert feet and inches to meters
  // 1 foot = 0.3048 meters, 1 inch = 0.0254 meters
  return feet * 0.3048 + inches * 0.0254;
};

/**
 * Check if a point (lat, lng) is inside a polygon geometry.
 *
 * :param latitude: Point latitude
 * :param longitude: Point longitude
 * :param polygonGeometry: GeoJSON Polygon or MultiPolygon geometry
 * :return: True if point is inside the polygon
 */
export const isPointInPolygon = (
  latitude: number,
  longitude: number,
  polygonGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
): boolean => {
  if (!polygonGeometry || !polygonGeometry.coordinates) return false;

  // point-in-geopolygon expects [lng, lat] format
  const point = [longitude, latitude];

  if (polygonGeometry.type === 'Polygon') {
    // For Polygon, coordinates is an array of rings (first is outer, rest are holes)
    return checkWithinPolygon.polygon(polygonGeometry.coordinates, point);
  } else if (polygonGeometry.type === 'MultiPolygon') {
    // For MultiPolygon, check if point is in any of the polygons
    return polygonGeometry.coordinates.some(
      (polygonCoords: GeoJSON.Position[][]) =>
        checkWithinPolygon.polygon(polygonCoords, point),
    );
  }

  return false;
};

/**
 * Build a floor height map from footprint features.
 * For floors with multiple entries (same floor_level), uses the one with highest floor_height.
 *
 * :param footprintFeatures: Array of GeoJSON features with floor_level and floor_height properties
 * :return: Map of floor_level to floor height in meters, sorted by floor_level
 */
export const buildFloorHeightMap = (
  footprintFeatures: GeoJSON.Feature[],
): Map<number, number> => {
  const floorMap = new Map<number, number>();

  footprintFeatures.forEach(feature => {
    const floorLevel = feature.properties?.floor_level;
    const floorHeight = feature.properties?.floor_height;

    if (floorLevel === undefined || floorLevel === null) return;

    const heightInMeters = parseFloorHeightToMeters(floorHeight);

    // Keep the highest floor_height for each floor_level
    if (
      !floorMap.has(floorLevel) ||
      heightInMeters > (floorMap.get(floorLevel) ?? 0)
    ) {
      floorMap.set(floorLevel, heightInMeters);
    }
  });

  // Return sorted by floor level
  return new Map(Array.from(floorMap.entries()).sort((a, b) => a[0] - b[0]));
};

/**
 * Estimate which floor a call is on based on altitude and ground elevation.
 *
 * The algorithm:
 * 1. Calculate height above ground = call altitude - ground elevation
 * 2. Iterate through floors from lowest to highest
 * 3. Accumulate floor heights until the cumulative height exceeds the call's height
 * 4. The floor where this occurs is the estimated floor
 *
 * :param callAltitude: Call altitude in meters (from GPS/device)
 * :param groundElevation: Ground elevation in meters (from Google Elevation API)
 * :param footprintFeatures: Array of GeoJSON features with floor data
 * :param verticalUncertainty: Optional vertical uncertainty in meters for min/max calculation
 * :return: FloorEstimationResult or null if estimation fails
 */
export const estimateFloor = (
  callAltitude: number,
  groundElevation: number,
  footprintFeatures: GeoJSON.Feature[],
  verticalUncertainty = 0,
): FloorEstimationResult | null => {
  if (
    callAltitude === null ||
    callAltitude === undefined ||
    groundElevation === null ||
    groundElevation === undefined
  ) {
    return null;
  }

  const floorHeightMap = buildFloorHeightMap(footprintFeatures);

  if (floorHeightMap.size === 0) {
    return null;
  }

  const heightAboveGround = callAltitude - groundElevation;
  const sortedFloorLevels = Array.from(floorHeightMap.keys()).sort(
    (a, b) => a - b,
  );
  // Use floor 0 as the ground reference when present. If floor 0 is missing
  // (common in some datasets), use the lowest non-negative floor (e.g. floor 1).
  const lowestNonNegativeFloorLevel = sortedFloorLevels.find(
    level => level >= 0,
  );
  const groundReferenceFloorLevel = floorHeightMap.has(0)
    ? 0
    : lowestNonNegativeFloorLevel;
  const hasGroundReferenceFloor = groundReferenceFloorLevel !== undefined;
  const aboveGroundFloorLevels = hasGroundReferenceFloor
    ? sortedFloorLevels.filter(level => level >= groundReferenceFloorLevel)
    : [];
  // Descending order so we walk nearest-below-ground first (-1, -2, -3...).
  const belowGroundFloorLevels = hasGroundReferenceFloor
    ? sortedFloorLevels
        .filter(level => level < groundReferenceFloorLevel)
        .sort((a, b) => b - a)
    : [];

  // Find the floor based on cumulative height
  const findFloorForHeight = (height: number): number => {
    // Anchor zero-height to the ground-reference floor (0 if present,
    // otherwise the lowest non-negative floor). Without this, buildings
    // with a basement (e.g., -1, 1, 2) incorrectly map ground-level
    // calls to -1 just because -1 is numerically the lowest floor.
    if (hasGroundReferenceFloor) {
      if (height >= 0) {
        let cumulativeHeight = 0;
        let highestEvaluatedFloor = groundReferenceFloorLevel;

        for (const floorLevel of aboveGroundFloorLevels) {
          const floorHeight =
            floorHeightMap.get(floorLevel) ?? DEFAULT_FLOOR_HEIGHT_METERS;
          if (height < cumulativeHeight + floorHeight) {
            return floorLevel;
          }
          cumulativeHeight += floorHeight;
          highestEvaluatedFloor = floorLevel;
        }

        // If height exceeds all known above-ground floors, clamp to top floor.
        return highestEvaluatedFloor;
      }

      // Height below ground: walk basement levels downward.
      if (belowGroundFloorLevels.length === 0) {
        return groundReferenceFloorLevel;
      }

      const depthBelowGround = Math.abs(height);
      let cumulativeDepth = 0;
      let lowestEvaluatedFloor = belowGroundFloorLevels[0];

      for (const floorLevel of belowGroundFloorLevels) {
        const floorHeight =
          floorHeightMap.get(floorLevel) ?? DEFAULT_FLOOR_HEIGHT_METERS;
        if (depthBelowGround < cumulativeDepth + floorHeight) {
          return floorLevel;
        }
        cumulativeDepth += floorHeight;
        lowestEvaluatedFloor = floorLevel;
      }

      // If depth exceeds known basement floors, clamp to lowest floor.
      return lowestEvaluatedFloor;
    }

    // Fallback for data without a floor 0: preserve original behavior.
    let cumulativeHeight = 0;
    let lastFloorLevel = sortedFloorLevels[0] ?? 0;
    for (const floorLevel of sortedFloorLevels) {
      const floorHeight =
        floorHeightMap.get(floorLevel) ?? DEFAULT_FLOOR_HEIGHT_METERS;
      if (height < cumulativeHeight + floorHeight) {
        return floorLevel;
      }
      cumulativeHeight += floorHeight;
      lastFloorLevel = floorLevel;
    }

    // If height exceeds all floors, return the highest floor.
    return lastFloorLevel;
  };

  const estimatedFloor = findFloorForHeight(heightAboveGround);

  // Calculate min/max floors based on vertical uncertainty
  const minEstimatedFloor = findFloorForHeight(
    heightAboveGround - verticalUncertainty,
  );
  const maxEstimatedFloor = findFloorForHeight(
    heightAboveGround + verticalUncertainty,
  );

  return {
    estimatedFloor,
    heightAboveGround,
    minEstimatedFloor,
    maxEstimatedFloor,
  };
};

/**
 * Check if a call is inside any of the building footprints and estimate its floor.
 *
 * :param callLatitude: Call latitude
 * :param callLongitude: Call longitude
 * :param callAltitude: Call altitude in meters
 * :param groundElevation: Ground elevation in meters
 * :param footprintFeatures: Array of footprint features
 * :param verticalUncertainty: Optional vertical uncertainty in meters
 * :return: FloorEstimationResult if call is inside a footprint, null otherwise
 */
export const estimateFloorForCallInBuilding = (
  callLatitude: number,
  callLongitude: number,
  callAltitude: number | undefined,
  groundElevation: number | null,
  footprintFeatures: GeoJSON.Feature[],
  verticalUncertainty = 0,
): FloorEstimationResult | null => {
  // Check if call has required data
  if (
    callAltitude === undefined ||
    callAltitude === null ||
    groundElevation === null
  ) {
    return null;
  }

  // Find a footprint that contains the call
  const containingFootprint = footprintFeatures.find(feature => {
    const geometry = feature.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
    return isPointInPolygon(callLatitude, callLongitude, geometry);
  });

  if (!containingFootprint) {
    return null;
  }

  // Estimate the floor
  return estimateFloor(
    callAltitude,
    groundElevation,
    footprintFeatures,
    verticalUncertainty,
  );
};

// Fallback fill colors by space_type, used only when a feature has no
// fill_color. Values mirror the client's space_type palette. Alpha is ignored
// downstream — fill opacity is applied separately via applyFillOpacity.
export const SPACE_TYPE_COLORS: Record<
  string,
  [number, number, number, number]
> = {
  classroom: [177, 66, 112, 255], // #B14270
  bathroom: [135, 206, 235, 255], // #87CEEB
  office: [252, 249, 109, 255], // #FCF96D
  public: [255, 255, 252, 255], // #FFFFFC
  room: [204, 204, 204, 255], // #CCCCCC
  storage: [182, 158, 114, 255], // #B69E72
  unit: [170, 170, 170, 255], // #AAAAAA
  utility: [231, 169, 54, 255], // #E7A936
  vertical: [190, 252, 67, 255], // #BEFC43
  opening: [77, 77, 77, 255], // #4D4D4D
  inaccessible: [77, 77, 77, 255], // #4D4D4D
  footprint: [77, 77, 77, 255], // #4D4D4D
  usng_10m: [77, 77, 77, 255], // #4D4D4D
  site: [77, 77, 77, 255], // #4D4D4D
  default: [200, 200, 200, 255],
};

export const getPolygonCentroid = (
  polygon: PolygonFeature,
): [number, number] => {
  const coords = polygon.geometry.coordinates[0];
  if (!coords || coords.length === 0) return [0, 0];

  let sumX = 0;
  let sumY = 0;
  const n = coords.length - 1;
  for (let i = 0; i < n; i++) {
    sumX += coords[i][0];
    sumY += coords[i][1];
  }
  return [sumX / n, sumY / n];
};

/**
 * Parse a `#rrggbb` hex string to an RGBA tuple (alpha 255), or null when the
 * value is missing or not valid 6-digit hex — so callers can fall through.
 */
export const parseHexColor = (
  hex: string | undefined,
): [number, number, number, number] | null => {
  if (!hex) return null;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
        255,
      ]
    : null;
};

/**
 * Convert a hex color to RGBA with the given alpha. Falls back to grey for a
 * missing or invalid value, so callers that need a guaranteed color are safe.
 */
export const hexToRgba = (
  hex: string,
  alpha = 255,
): [number, number, number, number] => {
  const rgb = parseHexColor(hex);
  return rgb ? [rgb[0], rgb[1], rgb[2], alpha] : [200, 200, 200, alpha];
};

export const getFeatureColor = (
  f: TileFeature,
): [number, number, number, number] => {
  const props = f.properties;

  // Use space_color if available (from tile data)
  // Using alpha = 255 (fully opaque) to avoid tile boundary artifacts
  if (props?.space_color) {
    return hexToRgba(props.space_color, 255);
  }

  // Fall back to space_type mapping with full opacity
  const spaceType = props?.space_type?.toLowerCase() || 'default';
  const color = SPACE_TYPE_COLORS[spaceType] || SPACE_TYPE_COLORS.default;
  // Override alpha to 255 for full opacity
  return [color[0], color[1], color[2], 255];
};

export const getFeatureLabel = (f: TileFeature): string => {
  const props = f.properties;
  return (
    props?.label_id ||
    props?.room_label ||
    props?.space_label ||
    props?.site_name ||
    ''
  );
};

// Room color categories based on label
const HALLWAY_KEYWORDS = ['corridor', 'hallway', 'passage', 'walkway', 'aisle'];

const IMPORTANT_ROOM_KEYWORDS = [
  'lobby',
  'vestibule',
  'foyer',
  'restaurant',
  'common',
  'central',
  'lounge',
  'reception',
  'waiting',
  'break room',
  'breakroom',
  'cafeteria',
  'dining',
  'gallery',
];

const STAIRS_KEYWORDS = [
  'stair',
  'stairs',
  'stairwell',
  'staircase',
  'elevator',
  'elevator lobby',
  'elevatorlobby',
];

const RESTROOM_KEYWORDS = ['restroom', 'bathroom', 'toilet', 'wc', 'lavatory'];

const UTILITY_KEYWORDS = [
  'boiler',
  'kitchen',
  'maintenance',
  'storage',
  'electrical',
  'laundry',
];

// Room fill colors based on label
const ROOM_COLORS = {
  hallway: [215, 215, 215, 255] as [number, number, number, number], // #D7D7D7
  rooms: [205, 188, 158, 255] as [number, number, number, number], // #CDBC9E
  restroom: [167, 224, 242, 255] as [number, number, number, number], // #A7E0F2
  important: [255, 242, 170, 255] as [number, number, number, number], // #FFF2AA
  stairs: [192, 197, 189, 255] as [number, number, number, number], // #C0C5BD
  utility: [238, 192, 114, 255] as [number, number, number, number], // #EEC072
};

/**
 * Apply a feature's own fill opacity to a base RGB color (fill only).
 * `opacity` is 0–1 from the source data; when absent, `defaultAlpha`
 * (0–255) preserves the layer's existing appearance.
 */
export const applyFillOpacity = (
  rgb: readonly number[],
  props: TileProperties | undefined,
  defaultAlpha = 255,
): [number, number, number, number] => {
  const opacity = props?.opacity;
  const alpha =
    typeof opacity === 'number'
      ? Math.round(Math.min(1, Math.max(0, opacity)) * 255)
      : defaultAlpha;
  return [rgb[0], rgb[1], rgb[2], alpha];
};

// Legacy last-resort base color derived from room-label keywords. Used only
// when a feature carries no fill_color, space_color, or known space_type.
const getRoomLabelColor = (
  props: TileProperties | undefined,
): [number, number, number, number] => {
  const label = (
    props?.label_id ||
    props?.room_label ||
    props?.space_label ||
    ''
  )
    .toLowerCase()
    .trim();
  const spaceType = (props?.space_type || '').toLowerCase().trim();

  const matchesKeywords = (keywords: string[]): boolean => {
    return keywords.some(
      keyword =>
        label.includes(keyword.toLowerCase()) ||
        spaceType.includes(keyword.toLowerCase()),
    );
  };

  // Check categories in order of specificity.
  if (matchesKeywords(STAIRS_KEYWORDS)) {
    return ROOM_COLORS.stairs;
  }
  if (matchesKeywords(RESTROOM_KEYWORDS)) {
    return ROOM_COLORS.restroom;
  }
  if (matchesKeywords(UTILITY_KEYWORDS)) {
    return ROOM_COLORS.utility;
  }
  if (matchesKeywords(IMPORTANT_ROOM_KEYWORDS)) {
    return ROOM_COLORS.important;
  }
  if (matchesKeywords(HALLWAY_KEYWORDS)) {
    return ROOM_COLORS.hallway;
  }
  return ROOM_COLORS.rooms;
};

// Get fill color for an individual room polygon.
// Precedence: per-feature fill_color → legacy space_color → space_type palette
// → room-label keywords (legacy fallback). The feature's own fill opacity is
// then applied (fill only).
export const getRoomFillColor = (
  f: TileFeature,
): [number, number, number, number] => {
  const props = f.properties;
  const spaceType = props?.space_type?.toLowerCase().trim();
  const paletteColor =
    spaceType && spaceType in SPACE_TYPE_COLORS
      ? SPACE_TYPE_COLORS[spaceType]
      : null;

  const base =
    parseHexColor(props?.fill_color) ??
    parseHexColor(props?.space_color) ??
    paletteColor ??
    getRoomLabelColor(props);

  return applyFillOpacity(base, props);
};

// Get border/outline color for an individual room polygon from its own
// border_color hex. Full opacity — fill opacity never applies to borders.
export const getRoomLineColor = (
  f: TileFeature,
): [number, number, number, number] => {
  return parseHexColor(f.properties?.border_color) ?? [0, 0, 0, 255];
};

// Fill color for a building footprint drawn from the tiles.
// Precedence: per-feature fill_color → FOOTPRINT_DEFAULT_FILL_COLOR. The
// feature's own fill opacity is applied (fill only).
export const getFootprintFillColor = (
  f: TileFeature,
): [number, number, number, number] => {
  const props = f.properties;
  const base = parseHexColor(props?.fill_color) ?? FOOTPRINT_DEFAULT_FILL_COLOR;
  return applyFillOpacity(base, props);
};

// Border color for a building footprint drawn from the tiles.
// Precedence: per-feature border_color → FOOTPRINT_DEFAULT_BORDER_COLOR
// (Coca-Cola red). Full opacity — a distinct seam so adjacent/overlapping
// footprints read as separate buildings instead of merging into one shape.
export const getFootprintLineColor = (
  f: TileFeature,
): [number, number, number, number] => {
  return (
    parseHexColor(f.properties?.border_color) ?? FOOTPRINT_DEFAULT_BORDER_COLOR
  );
};

// Get line color for individual polygon (can be customized per feature)
export const getFeatureLineColor = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _f: TileFeature,
): [number, number, number, number] => {
  // Default line color - can be customized based on feature properties
  return [80, 80, 80, 255];
};

// Get line width for individual polygon (can be customized per feature)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getFeatureLineWidth = (_f: TileFeature): number => {
  // Default line width - can be customized based on feature properties
  return 1;
};

// Helper to create normalized building key string
// Removes spaces and lowercases to handle inconsistent data (e.g., "LansdowneResort" vs "Lansdowne Resort")
export const getBuildingKey = (props: TileProperties): string => {
  const siteName = (props.site_name || '').replace(/\s+/g, '').toLowerCase();
  const building = (props.building || '').replace(/\s+/g, '').toLowerCase();
  const address = (props.street_address || '')
    .replace(/\s+/g, '')
    .toLowerCase();
  return `${siteName}|${building}|${address}`;
};

/** Fetch building GeoJSON from the consolidated site endpoint. */
// The API returns a FeatureCollection with all features; we categorize them by geometry type
export const fetchBuildingGeoJSON = async (
  siteId: string,
): Promise<{
  footprint: GeoJSON.FeatureCollection | null;
  polygon: GeoJSON.FeatureCollection | null;
  point: GeoJSON.FeatureCollection | null;
  siteId: string;
}> => {
  const url = `${GEOJSON_SITE_URL}/${siteId}`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return { footprint: null, polygon: null, point: null, siteId };
    }

    const data = await response.json();

    // The API returns a FeatureCollection - categorize features by geometry type and space_type
    const features: GeoJSON.Feature[] = data.features || [];

    // Separate features by type
    const footprintFeatures: GeoJSON.Feature[] = [];
    const polygonFeatures: GeoJSON.Feature[] = [];
    const pointFeatures: GeoJSON.Feature[] = [];

    features.forEach((feature: GeoJSON.Feature) => {
      const geomType = feature.geometry?.type;
      const spaceType = (feature.properties?.space_type || '').toLowerCase();

      if (geomType === 'Point') {
        pointFeatures.push(feature);
      } else if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
        // Footprint features have space_type === 'footprint'
        if (spaceType === 'footprint') {
          footprintFeatures.push(feature);
        } else {
          polygonFeatures.push(feature);
        }
      }
    });

    return {
      footprint:
        footprintFeatures.length > 0
          ? { type: 'FeatureCollection', features: footprintFeatures }
          : null,
      polygon:
        polygonFeatures.length > 0
          ? { type: 'FeatureCollection', features: polygonFeatures }
          : null,
      point:
        pointFeatures.length > 0
          ? { type: 'FeatureCollection', features: pointFeatures }
          : null,
      siteId,
    };
  } catch {
    return { footprint: null, polygon: null, point: null, siteId };
  }
};
